import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from '../../lib/redux/hooks';
import { LogoSpinnerComponent } from "../../lib/spinner/spinnerLogo";
import { setSelectedCustomerActivities, setSelectedCustomerCode, setSelectedCustomerId, setSelectedCustomerName, setSelectedCustomerNorm, setSelectedCustomerRegion, setSelectedCustomerUserId } from "../../session/slice";
import { ActiveIcon } from "../../ui/icons/activeIcon";
import { EditIcon } from "../../ui/icons/edit";
import { InactiveIcon } from "../../ui/icons/inactiveIcon";
import { SquaredTickIcon } from "../../ui/icons/SquaredTickIcon";
import { formatterDate, getLabelACtivity, normativaView } from "../../utils";
import { setCustomerIdFilter } from "../schedaCalcolo/slice";
import { getCustomersByIdResponseDTO } from "./dto";
import { resetCompanyCenterByIds } from "./feature/companyCenter/slice";
import { resetContactById } from "./feature/contact/slice";
import { resetLegalRepresentativeByIds } from "./feature/legalRepresentative/slice";
import { setSelectedCustomerTab } from "./feature/slice";

export function ClientiList() {
    const customerState = useAppSelector(state => state.customer)
    const dispatch = useAppDispatch()
    const navigate = useNavigate()
    const customers = customerState.findAllCustomersResponse?.data ?? []
    const isInitialState = customerState.findAllCustomersStatus === 'idle'

    const getCustomerHeadQuarterLabel = (customer: getCustomersByIdResponseDTO) => {
        if (customer.headQuartersInfo?.length) {
            return customer.headQuartersInfo.map((headQuarter) => headQuarter.code).join(', ')
        }

        return customer.headQuarterIds || '—'
    }

    const onSelection = (customer: getCustomersByIdResponseDTO) => {
        dispatch(setCustomerIdFilter(customer.id))
        dispatch(setSelectedCustomerId(customer.id))
        dispatch(setSelectedCustomerUserId(customer.userId))
        dispatch(setSelectedCustomerName(customer.companyName))
        dispatch(setSelectedCustomerCode(customer.clientCode))
        dispatch(setSelectedCustomerRegion(customer.countryRegion))
        dispatch(setSelectedCustomerNorm(customer.norm))
        dispatch(setSelectedCustomerActivities(customer.biologicalActivity?.map(ba => ba.activity)))
    }

    if (customerState.findAllCustomersStatus === 'loading') {
        return <LogoSpinnerComponent fullScreen />
    }

    return (
        <div className="w-full overflow-x-auto rounded-xl border border-neutral-200 shadow-sm">
            <table className="w-full border-collapse text-sm">
                <thead>
                    <tr className="bg-brandPrimary-600 text-white uppercase text-xs tracking-wide h-[52px]">
                        <th className="px-3 py-3 text-left font-semibold whitespace-nowrap rounded-tl-xl">Cod. Op.</th>
                        <th className="px-3 py-3 text-left font-semibold whitespace-nowrap">Ragione Sociale</th>
                        <th className="px-3 py-3 text-left font-semibold whitespace-nowrap">Norma</th>
                        <th className="px-3 py-3 text-left font-semibold whitespace-nowrap">Sede</th>
                        <th className="px-3 py-3 text-left font-semibold whitespace-nowrap">CUAA</th>
                        <th className="px-3 py-3 text-left font-semibold whitespace-nowrap">P.IVA</th>
                        <th className="px-3 py-3 text-left font-semibold whitespace-nowrap">Attività</th>
                        <th className="px-3 py-3 text-left font-semibold whitespace-nowrap">Categoria Prodotti</th>
                        <th className="px-3 py-3 text-left font-semibold whitespace-nowrap">Regione</th>
                        <th className="px-3 py-3 text-left font-semibold whitespace-nowrap">Data Ingresso</th>
                        <th className="px-3 py-3 text-left font-semibold whitespace-nowrap">Data Uscita</th>
                        <th className="px-3 py-3 text-center font-semibold whitespace-nowrap">Status</th>
                        <th className="px-3 py-3 text-center font-semibold whitespace-nowrap rounded-tr-xl"></th>
                    </tr>
                </thead>
                <tbody>
                    {customers.length === 0 ? (
                        <tr>
                            <td colSpan={13} className="px-3 py-3 text-center text-neutral-500 bg-white">
                                {isInitialState
                                    ? 'Applica un filtro o cambia il filtro per cercare determinati clienti'
                                    : 'Nessun cliente trovato'}
                            </td>
                        </tr>
                    ) :
                        customers.map((customer, rowIndex) => (
                            <tr
                                key={customer.userId}
                                className={`border-b border-neutral-200 transition-colors duration-150 hover:bg-brandPrimary-50 ${rowIndex % 2 === 0 ? 'bg-white' : 'bg-neutral-50'}`}
                            >
                                <td className="px-3 py-2 text-blue-800 font-bold whitespace-nowrap">
                                    {customer.clientCode}
                                </td>
                                <td className="px-3 py-2 text-neutral-700 font-semibold whitespace-nowrap">
                                    {customer.companyName}
                                </td>
                                <td className="px-3 py-2 text-neutral-700 whitespace-nowrap">
                                    {normativaView
                                        .filter(({ value }) => value === customer.norm)
                                        .map(el => (
                                            <span
                                                key={el.value}
                                                className="inline-block bg-brandPrimary-200 text-brandPrimary-700 text-xs font-medium px-3 py-2 rounded-full"
                                            >
                                                {el.label}
                                            </span>
                                        ))}
                                </td>
                                <td className="px-3 py-2 text-neutral-700 whitespace-nowrap">{getCustomerHeadQuarterLabel(customer)}</td>
                                <td className="px-3 py-2 text-neutral-700 whitespace-nowrap font-mono text-xs">
                                    {customer.cuaa}
                                </td>
                                <td className="px-3 py-2 text-neutral-700 whitespace-nowrap font-mono text-xs">
                                    {customer.vat}
                                </td>
                                <td className="px-3 py-2 text-neutral-700 min-w-[130px]">
                                    {customer.biologicalActivity?.length > 0
                                        ? customer.biologicalActivity.map((activity, i) => (
                                            <p key={i} className="text-xs leading-5">
                                                {getLabelACtivity(activity.activity, 'attivita_bio')}
                                            </p>
                                        ))
                                        : <span className="text-neutral-400">—</span>
                                    }
                                </td>
                                <td className="px-3 py-2 text-neutral-700 min-w-[150px]">
                                    {customer.biologicalActivity?.length > 0
                                        ? customer.biologicalActivity.map((activity, i) => (
                                            <p key={i} className="text-xs leading-5">
                                                {getLabelACtivity(activity.category[0], 'categorie_prodotti')}
                                            </p>
                                        ))
                                        : <span className="text-neutral-400">—</span>
                                    }
                                </td>
                                <td className="px-3 py-2 text-neutral-700 whitespace-nowrap">
                                    {customer.countryRegion}
                                </td>
                                <td className="px-3 py-2 text-neutral-700 whitespace-nowrap text-xs">
                                    {formatterDate(customer.startingDate)}
                                </td>
                                <td className="px-3 py-2 text-neutral-700 whitespace-nowrap text-xs">
                                    {customer.endingDate ? formatterDate(customer.endingDate) : <span className="text-neutral-400">—</span>}
                                </td>
                                <td className="px-3 py-2 text-center">
                                    {!customer.endingDate ? <ActiveIcon /> : <InactiveIcon />}
                                </td>
                                <td className="px-3 py-2 w-[60px]">
                                    <div className="flex items-center justify-center gap-2">
                                        <button
                                            className="relative group p-1 rounded hover:bg-neutral-100 transition-colors"
                                            onClick={() => { onSelection(customer); navigate('/'); }}
                                        >
                                            <SquaredTickIcon />
                                            <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 hidden group-hover:block px-2 py-1 text-xs text-white bg-neutral-800 rounded whitespace-nowrap pointer-events-none z-10">
                                                Seleziona Cliente
                                            </span>
                                        </button>
                                        <button
                                            className="relative group p-1 rounded hover:bg-neutral-100 transition-colors"
                                            onClick={() => {
                                                onSelection(customer);
                                                dispatch(setSelectedCustomerTab(0));
                                                dispatch(resetCompanyCenterByIds());
                                                dispatch(resetLegalRepresentativeByIds());
                                                dispatch(resetContactById());
                                                navigate('/edit-customer/' + customer.id);
                                            }}
                                        >
                                            <EditIcon color={"Orange"} size={20} />
                                            <span className="absolute bottom-full right-0 mb-1 hidden group-hover:block px-2 py-1 text-xs text-white bg-neutral-800 rounded whitespace-nowrap pointer-events-none z-10">
                                                Modifica Cliente
                                            </span>
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                </tbody>
            </table>
        </div>
    )
}